// @flow
import * as React from 'react';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import type { PrismicContactForm } from '~schema';
import hasRichTextFieldValue from '~plugins/prismic/helpers/hasRichTextFieldValue';

import type { ContactFormValues } from './types';
import messages from './messages';

const useContactFormValidationSchema = (node: ?PrismicContactForm) => {
  const intl = useIntl();
  return React.useMemo(
    () =>
      yup.object<ContactFormValues>({
        name: yup
          .string()
          .required(
            node?.data?.error_required?.text ||
              intl.formatMessage(messages.errors.required),
          )
          .max(
            80,
            node?.data?.error_max_characters?.text ||
              intl.formatMessage(messages.errors.maxCharacters, {
                count: intl.formatNumber(80),
              }),
          ),
        email: yup
          .string()
          .required(
            node?.data?.error_required?.text ||
              intl.formatMessage(messages.errors.required),
          )
          .email(
            node?.data?.error_required?.text ||
              intl.formatMessage(messages.errors.email),
          )
          .max(
            100,
            node?.data?.error_max_characters?.text ||
              intl.formatMessage(messages.errors.maxCharacters, {
                count: intl.formatNumber(100),
              }),
          ),
        phone: yup
          .string()
          .min(
            9,
            node?.data?.error_min_characters?.text ||
              intl.formatMessage(messages.errors.minCharacters, {
                count: intl.formatNumber(9),
              }),
          )
          .max(
            30,
            node?.data?.error_max_characters?.text ||
              intl.formatMessage(messages.errors.maxCharacters, {
                count: intl.formatNumber(30),
              }),
          )
          .required(
            node?.data?.error_required?.text ||
              intl.formatMessage(messages.errors.required),
          ),
        body: yup
          .string()
          .required(
            node?.data?.error_required?.text ||
              intl.formatMessage(messages.errors.required),
          )
          .max(
            32000,
            node?.data?.error_max_characters?.text ||
              intl.formatMessage(messages.errors.maxCharacters, {
                count: intl.formatNumber(32000),
              }),
          ),
        acceptPrivacyPolicy:
          // add validation only when there is label, as it will only be shown then
          hasRichTextFieldValue(node?.data?.label_accept_privacy_policy)
            ? yup
                .boolean()
                .test(
                  'isTrue',
                  node?.data?.error_must_accept_privacy_policy?.text ||
                    intl.formatMessage(messages.errors.mustAcceptPrivacyPolicy),
                  value => value === true,
                )
            : yup.mixed(),
        inputSector: yup.string().nullable(true),
        gclid: yup.string().nullable(true),
      }),
    [node],
  );
};

export default useContactFormValidationSchema;
